import { authApi } from '@/api/endpoints'
import type { CaptchaConfig } from '@/types'

/**
 * #240 — Google reCAPTCHA (Enterprise, score-based) for the dashboard's login and password-reset
 * request.
 *
 * The server decides everything: `GET /auth/captcha-config` says the mode and gives the public site
 * key (null when the mode is `off`, and then nothing is loaded from Google). A token is taken fresh
 * on every submit — tokens are single-use and expire after two minutes.
 *
 * Never throws and never blocks a submit: any failure (config unreachable, script blocked by an
 * ad-blocker, Google slow) yields no token. The server then lets the request through in `verify`
 * and refuses it with a clear message in `enforce` — the client never makes that call itself.
 */
export type RecaptchaAction = 'login' | 'password_reset'

interface Grecaptcha {
  enterprise: {
    ready: (cb: () => void) => void
    execute: (siteKey: string, options: { action: string }) => Promise<string>
  }
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha
  }
}

const SCRIPT_ID = 'recaptcha-enterprise'
const LOAD_TIMEOUT_MS = 10_000
const EXECUTE_TIMEOUT_MS = 8_000

let configPromise: Promise<CaptchaConfig | null> | null = null
let scriptPromise: Promise<Grecaptcha> | null = null

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('recaptcha timeout')), ms)
    promise.then(
      (value) => { clearTimeout(timer); resolve(value) },
      (err) => { clearTimeout(timer); reject(err) },
    )
  })
}

function fetchConfig(): Promise<CaptchaConfig | null> {
  return Promise.resolve()
    .then(() => authApi.captchaConfig())
    .catch(() => null)
}

function isOn(config: CaptchaConfig | null): config is CaptchaConfig & { siteKey: string } {
  return !!config && config.mode !== 'off' && typeof config.siteKey === 'string' && config.siteKey !== ''
}

function loadScript(siteKey: string): Promise<Grecaptcha> {
  if (scriptPromise) return scriptPromise
  const loading = new Promise<Grecaptcha>((resolve, reject) => {
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.async = true
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`
    script.onload = () => {
      const g = window.grecaptcha
      if (g?.enterprise) g.enterprise.ready(() => resolve(g))
      else reject(new Error('recaptcha script loaded without grecaptcha.enterprise'))
    }
    script.onerror = () => reject(new Error('recaptcha script failed to load'))
    document.head.appendChild(script)
  })
  scriptPromise = withTimeout(loading, LOAD_TIMEOUT_MS).catch((err) => {
    // Let the next submit try again from scratch.
    scriptPromise = null
    document.getElementById(SCRIPT_ID)?.remove()
    throw err
  })
  return scriptPromise
}

/** Refresh the config (the owner can switch the mode at any time) and warm the script up, so the
 *  first submit does not wait for it. Resolves to whether reCAPTCHA is on, for the notice. */
export async function preloadRecaptcha(): Promise<boolean> {
  configPromise = fetchConfig()
  const config = await configPromise
  if (!isOn(config)) return false
  loadScript(config.siteKey).catch(() => undefined)
  return true
}

/** A fresh token for `action`, or undefined when reCAPTCHA is off or unavailable in this browser. */
export async function getRecaptchaToken(action: RecaptchaAction): Promise<string | undefined> {
  try {
    if (!configPromise) configPromise = fetchConfig()
    const config = await configPromise
    if (config === null) configPromise = null // unreachable: ask again next time
    if (!isOn(config)) return undefined
    const g = await loadScript(config.siteKey)
    const token = await withTimeout(g.enterprise.execute(config.siteKey, { action }), EXECUTE_TIMEOUT_MS)
    return typeof token === 'string' && token !== '' ? token : undefined
  } catch {
    return undefined
  }
}

/** Test hook: forget cached config and script state. */
export function resetRecaptchaForTests(): void {
  configPromise = null
  scriptPromise = null
  document.getElementById(SCRIPT_ID)?.remove()
  delete window.grecaptcha
}
